import React, { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import IForm from '../../../types/IForm';
import { ActionState, ErrorState } from './useContentWrapper';
import { getPosition, Position } from './ContentWrapper.utils';
import { ContentWrapperActionType, ContentWrapperBlocks } from './ContentWrapper.types';

type SharedContextAndProviderProps = {
  onDeleteSuccess(uuid: string): void;
  onMovingFailure(uuid: string): void;
  onMovingSuccess(actionKey: string, uuid: string, elementType: string): void;
  refetch(uuid: string): Promise<void>;
  nextBlockIsRichtext: boolean;
  index: number;
  baseIndex?: number;
  error?: ErrorState | null;
  action?: ActionState | null;
  numBlocks: number;
  setAction: React.Dispatch<React.SetStateAction<ActionState | null>>;
  setError: React.Dispatch<React.SetStateAction<ErrorState | null>>;
};

export type ContentWrapperContext = SharedContextAndProviderProps & {
  actions: Record<ContentWrapperActionType, IForm>;
  position: Position | null;
  addBlock: (newBlock: ContentWrapperBlocks, position: number) => void;
};

export type ContentWrapperContextProvider = SharedContextAndProviderProps & {
  block: ContentWrapperBlocks;
  addBlock: (newBlock: ContentWrapperBlocks, position: number) => void;
};

export const ContentWrapperContext = createContext<ContentWrapperContext | null>(null);
export const useContentWrapperContext = () => useContext(ContentWrapperContext);

export function ContentWrapperContextProvider({
  children,
  block,
  index,
  baseIndex,
  error,
  action,
  setAction,
  addBlock,
  setError,
  numBlocks,
  nextBlockIsRichtext,
  onDeleteSuccess,
  onMovingFailure,
  onMovingSuccess,
  refetch,
}: PropsWithChildren<ContentWrapperContextProvider>) {
  const contentWrapperProps = useMemo(
    () => ({
      actions: block.actions,
      baseIndex,
      error,
      index,
      position: getPosition(index, baseIndex, block.elementType, false, undefined),
      action,
      setAction,
      addBlock,
      setError,
      nextBlockIsRichtext,
      numBlocks,
      onDeleteSuccess,
      onMovingFailure,
      onMovingSuccess,
      refetch,
    }),
    [
      block.actions,
      block.elementType,
      baseIndex,
      error,
      index,
      action,
      setAction,
      addBlock,
      setError,
      nextBlockIsRichtext,
      numBlocks,
      onDeleteSuccess,
      onMovingFailure,
      onMovingSuccess,
      refetch,
    ],
  );

  return <ContentWrapperContext.Provider value={contentWrapperProps}>{children}</ContentWrapperContext.Provider>;
}
